import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Field, Modal, Segments, useToast } from "@signet/ui";
import { addRate, melbourneToday, rateLabel } from "../shared/dates.ts";
import { amountText, dayText, exKeys, markPaid, price, type Item } from "./api.ts";
import { CurrencySelect, DayPicker } from "./controls.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Mark as Paid (Jay, 2026-10-21). The price comes filled in, and the next
   expiry is the expiry date plus the item's rate. When it is paid on a day
   other than its expiry date (Melbourne's today), a switch picks whether the
   rate is added to the original expiry date, the default, or to today.
   ────────────────────────────────────────────────────────────────────────── */

type Base = "expiry" | "today";

function nextFrom(item: Item, base: Base, today: string): string | null {
  if (!item.rateCount || !item.rateUnit) return null;
  const from = base === "today" || !item.nextExpiry ? today : item.nextExpiry;
  return addRate(from, item.rateCount, item.rateUnit);
}

function PaidForm({ item, onClose }: { item: Item; onClose: () => void }) {
  const today = melbourneToday();
  const offDay = !!item.nextExpiry && item.nextExpiry !== today;
  const [base, setBase] = useState<Base>("expiry");
  const [amount, setAmount] = useState(item.price === null ? "" : amountText(item.price));
  const [currency, setCurrency] = useState(item.currency);
  const [nextExpiry, setNextExpiry] = useState<string | null>(() => nextFrom(item, "expiry", today));
  const [notes, setNotes] = useState("");
  const client = useQueryClient();
  const toast = useToast();

  const value = Number(amount.replace(/,/g, ""));
  const valid = amount.trim() !== "" && Number.isFinite(value) && value >= 0 && !!nextExpiry;

  const pay = useMutation({
    mutationFn: () => markPaid(item.id, { amount: Math.round(value * 100) / 100, currency, nextExpiry: nextExpiry!, notes: notes.trim() || undefined }),
    onSuccess: async ({ payment }) => {
      await client.invalidateQueries({ queryKey: exKeys.all });
      toast.success(`${item.name} marked paid`, `${price(payment.amount, payment.currency)}, next expiry ${dayText(payment.nextExpiry!)}`);
      onClose();
    },
    onError: (error) => toast.error("Couldn't mark it paid", (error as Error).message),
  });

  const pickBase = (next: Base) => {
    setBase(next);
    const day = nextFrom(item, next, today);
    if (day) setNextExpiry(day);
  };

  return (
    <form
      id="ex-paid-form"
      className="ex-paid"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && !pay.isPending) pay.mutate();
      }}
    >
      <Field label="Price">
        <span className="ex-pair ex-paid-price">
          <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" aria-label="Price" autoFocus />
          <CurrencySelect value={currency} onChange={setCurrency} />
        </span>
      </Field>

      {offDay && item.rateCount && item.rateUnit ? (
        <Field label={`Add ${rateLabel(item.rateCount, item.rateUnit)} to`}>
          <Segments
            label="Add the expiry rate to"
            value={base}
            onChange={pickBase}
            options={[
              { value: "expiry", label: `Original expiry date (${dayText(item.nextExpiry!, false)})` },
              { value: "today", label: `Today (${dayText(today, false)})` },
            ]}
          />
        </Field>
      ) : null}

      <Field label="Next expiry" hint={!item.rateCount || !item.rateUnit ? "This item has no expiry rate, so pick the date." : undefined}>
        <DayPicker label="Next expiry" value={nextExpiry} onChange={setNextExpiry} clearable={false} />
      </Field>

      <Field label="Notes (optional)">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Card used, invoice number…" />
      </Field>
    </form>
  );
}

export function MarkPaidModal({ item, onClose }: { item: Item | null; onClose: () => void }) {
  return (
    <Modal
      open={!!item}
      onClose={onClose}
      title={item ? `Mark ${item.name} as paid` : ""}
      description={item?.nextExpiry ? `Expires ${dayText(item.nextExpiry)}` : undefined}
      footer={
        <>
          <button type="button" className="secondary-button compact" onClick={onClose}>Cancel</button>
          <button type="submit" form="ex-paid-form" className="button brass compact">Mark as Paid</button>
        </>
      }
    >
      {item ? <PaidForm key={item.id} item={item} onClose={onClose} /> : null}
    </Modal>
  );
}
